/**
 * Preparación de las fotos de DNI en el browser: reducción a tamaño de envío,
 * franja del MRZ con preproceso (lección de agilpost: greyscale+contraste o el
 * OCR lee mal), rotación de auxilio, y la REDACCIÓN del Nº de Trámite (relleno
 * negro destructivo re-encodeado — la imagen que sale ya no lo contiene).
 */

import { OcrWord } from "./tesseractLoader";
import { PalabraOcr } from "./dniMatch";
import { DefinicionDeRotulo, ROTULOS, valorBajoDeRotulo } from "./reader/frente";

export interface RedactRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export async function fileToImage(file: File): Promise<HTMLImageElement> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("file_read_failed"));
    reader.readAsDataURL(file);
  });
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_decode_failed"));
    img.src = dataUrl;
  });
}

/**
 * Las dos caras en UNA imagen, el frente arriba y el dorso abajo.
 *
 * Es lo que se manda: quien lo recibe abre un archivo y ve el documento
 * entero, en vez de tener que emparejar dos archivos.
 *
 * Las dos se llevan al mismo ancho —el mayor de los dos— para que ninguna
 * quede más chica que la otra, y van separadas por una franja blanca para que
 * se vea dónde termina una y empieza la otra.
 */
export function apilarCaras(
  arriba: HTMLCanvasElement,
  abajo: HTMLCanvasElement,
  separacion = 16
): HTMLCanvasElement {
  const ancho = Math.max(arriba.width, abajo.width);
  const alto = (c: HTMLCanvasElement) => Math.round((c.height * ancho) / c.width);
  const out = document.createElement("canvas");
  out.width = ancho;
  out.height = alto(arriba) + separacion + alto(abajo);
  const ctx = out.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(arriba, 0, 0, ancho, alto(arriba));
  ctx.drawImage(abajo, 0, alto(arriba) + separacion, ancho, alto(abajo));
  return out;
}

export function rotate90(c: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = c.height;
  out.height = c.width;
  const ctx = out.getContext("2d")!;
  ctx.translate(out.width, 0);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(c, 0, 0);
  return out;
}

/**
 * El archivo elegido, ya derecho y en tamaño de trabajo.
 *
 * Dos correcciones que no son opcionales con fotos de celular:
 *
 *  - ORIENTACIÓN EXIF. Una foto sacada con el teléfono guarda los píxeles como
 *    salieron del sensor y anota aparte cómo hay que girarlos. `createImageBitmap`
 *    con `from-image` aplica ese giro; dibujar la imagen sin más lo ignora en
 *    parte de los navegadores, y entonces se ve acostada.
 *  - TARJETA APAISADA. El DNI es más ancho que alto. Si después de enderezar la
 *    foto sigue siendo vertical, es que se fotografió girada: el texto queda a
 *    90° y el OCR no lee NADA. Se la gira para que quede horizontal.
 */
export async function fileToCanvas(file: File, maxDim = 2000): Promise<HTMLCanvasElement> {
  let fuente: ImageBitmap | HTMLImageElement;
  let ancho: number;
  let alto: number;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    fuente = bitmap;
    ancho = bitmap.width;
    alto = bitmap.height;
  } catch {
    const img = await fileToImage(file);
    fuente = img;
    ancho = img.naturalWidth;
    alto = img.naturalHeight;
  }

  const escala = Math.min(1, maxDim / Math.max(ancho, alto));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(ancho * escala);
  canvas.height = Math.round(alto * escala);
  canvas.getContext("2d")!.drawImage(fuente as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  if ("close" in fuente) (fuente as ImageBitmap).close();

  // NO se gira. Acá se giraba un cuarto de vuelta toda foto vertical, de cuando
  // no existía la cascada; hoy la cascada prueba las cuatro orientaciones y
  // elige la que cierra dígitos de control o encuentra campos, que es una
  // medición y no una suposición sobre cómo sostuvo el teléfono la persona.
  //
  // Y hacía daño: una foto de las DOS caras apiladas es vertical, entraba
  // acostada, y con el MRZ de costado no hay bloque que reconocer — el corte en
  // dos no se detectaba nunca.
  return canvas;
}

/** Canvas reducido a maxDim en el lado mayor (tamaño de trabajo y de envío). */
export function imageToCanvas(img: HTMLImageElement, maxDim = 2000): HTMLCanvasElement {
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * Copia ampliada y en escala de grises para pasarle al OCR.
 *
 * No es un lujo: medido con `scripts/test/ocr-dni.js` sobre fotos reales, el
 * frente a tamaño original NO se lee (el número de documento sale mezclado) y a
 * 2x sí. Lo que se ENVÍA sigue siendo el canvas original —ampliar no agrega
 * información, solo ayuda al reconocedor—, así que las cajas que devuelve el
 * OCR vienen en coordenadas de ESTA copia y hay que dividirlas por el factor
 * antes de dibujar sobre el original (ver findTramiteRegions).
 */
/**
 * El ancho al que conviene leer el frente entero, MEDIDO.
 *
 * No es un número de gusto: se probó la misma foto a 800, 1200, 1600, 2000,
 * 2456, 3500 y 4912 píxeles de ancho. A 1600 salen todos los datos —apellido,
 * nombre, número, fecha, nacionalidad— en las dos fotos de prueba, una de 1280
 * y otra de 2456. Más grande se pierden, y de a uno: a 2456 se cae el apellido,
 * a 4912 no queda más que la nacionalidad.
 *
 * La razón es el fondo de seguridad. Ampliar no agranda solo el texto: agranda
 * también el guilloche, y a partir de cierto tamaño sus trazos miden lo que una
 * letra y el OCR los lee como si lo fueran. Por eso el frente se lleva a un
 * ancho de trabajo en vez de multiplicarse por un factor fijo: una foto chica
 * se agranda, una grande se achica, y las dos terminan donde se leen.
 */
export const ANCHO_OCR_FRENTE = 1600;

/** Cuánto hay que escalar esta imagen para leerla: puede ser menor que 1. */
export function factorOcr(c: HTMLCanvasElement, anchoObjetivo = ANCHO_OCR_FRENTE): number {
  return c.width > 0 ? anchoObjetivo / c.width : 1;
}

export function upscaleForOcr(
  c: HTMLCanvasElement,
  factor = factorOcr(c)
): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(c.width * factor));
  out.height = Math.max(1, Math.round(c.height * factor));
  const ctx = out.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(c, 0, 0, out.width, out.height);

  const data = ctx.getImageData(0, 0, out.width, out.height);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    const g = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
    px[i] = px[i + 1] = px[i + 2] = g;
  }
  ctx.putImageData(data, 0, 0);
  return out;
}

export function rotate180(c: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = c.width;
  out.height = c.height;
  const ctx = out.getContext("2d")!;
  ctx.translate(out.width, out.height);
  ctx.rotate(Math.PI);
  ctx.drawImage(c, 0, 0);
  return out;
}

/**
 * Franja inferior del dorso (donde vive el MRZ) con preproceso para OCR-B:
 * greyscale + estiramiento de contraste + upscale 2x.
 */
export function mrzStripCanvas(c: HTMLCanvasElement, fraction = 0.45): HTMLCanvasElement {
  const stripH = Math.round(c.height * fraction);
  const strip = document.createElement("canvas");
  strip.width = c.width * 2;
  strip.height = stripH * 2;
  const ctx = strip.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(c, 0, c.height - stripH, c.width, stripH, 0, 0, strip.width, strip.height);

  const data = ctx.getImageData(0, 0, strip.width, strip.height);
  const px = data.data;
  let min = 255;
  let max = 0;
  for (let i = 0; i < px.length; i += 4) {
    const g = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
    px[i] = px[i + 1] = px[i + 2] = g;
    if (g < min) min = g;
    if (g > max) max = g;
  }
  const range = Math.max(1, max - min);
  for (let i = 0; i < px.length; i += 4) {
    const v = ((px[i] - min) * 255) / range;
    px[i] = px[i + 1] = px[i + 2] = v;
  }
  ctx.putImageData(data, 0, 0);
  return strip;
}

/**
 * Zonas del Nº de Trámite a tapar, con criterio CONSERVADOR.
 *
 * Tapar de más arruina el documento para quien lo recibe: en SADAIC el frente
 * lo lee una persona, y un rectángulo negro sobre el código de barras o sobre
 * el número de documento le saca justamente lo que necesita. Pasó: la primera
 * versión buscaba "cualquier palabra con varios dígitos cerca del rótulo" y
 * terminó tapando el código de barras.
 *
 * Así que se tapa solo lo que es reconociblemente un número de trámite:
 *   - once dígitos seguidos, que es su formato; o
 *   - un número de cinco o más dígitos ubicado en la MISMA línea del rótulo
 *     "Trámite" y a su derecha.
 * Nunca el número de documento del titular, y nunca una caja tan ancha que no
 * pueda ser un número. Lo que se escape lo tapa la persona con el recuadro
 * manual, que es preferible a tapar de más automáticamente.
 */
export function findTramiteRegions(
  words: OcrWord[],
  margin = 8,
  scale = 1,
  /** Documento del titular: jamás se tapa. */
  dniTitular = ""
): RedactRect[] {
  const norm = (t: string) =>
    t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  const digitosDe = (t: string) => t.replace(/\D+/g, "");
  const dni = digitosDe(dniTitular).replace(/^0+/, "");

  const anchoImagen = Math.max(...words.map((w) => w.bbox.x1), 1);
  const rotulos = words.filter((w) => norm(w.text).includes("TRAMITE"));

  const esElDocumento = (w: OcrWord) => {
    const d = digitosDe(w.text).replace(/^0+/, "");
    return dni.length > 0 && d === dni;
  };
  const mismaLinea = (a: OcrWord, b: OcrWord) => {
    const centroA = (a.bbox.y0 + a.bbox.y1) / 2;
    return centroA >= b.bbox.y0 && centroA <= b.bbox.y1;
  };

  const rects: RedactRect[] = [];
  for (const w of words) {
    if (esElDocumento(w)) continue;
    const d = digitosDe(w.text);
    // Un número de 7 u 8 dígitos tiene la forma de un DOCUMENTO y no se tapa
    // nunca, esté donde esté. El trámite tiene once.
    //
    // Esta regla existe porque la protección anterior —conocer el número del
    // titular— está apagada justo cuando más hace falta: creando un autor nuevo
    // todavía no hay ningún documento cargado contra el cual comparar. Y tapar
    // el número de documento arruina la foto para quien la recibe en SADAIC,
    // que es una persona leyéndola.
    if (d.length >= 7 && d.length <= 8) continue;
    if (d.length < 9) continue;
    // El trámite es un NÚMERO. Un pedazo de código de barras OCReado es una
    // mezcla de letras, barras y dígitos, y con solo pedir "nueve o más
    // dígitos" entraba en la red: se tapaba un trozo del PDF417 y el trámite
    // quedaba a la vista. Tapar el código además destruye la única fuente con
    // corrección de errores que tiene el documento.
    const limpio = w.text.replace(/\s/g, "");
    if (limpio.length > 0 && d.length / limpio.length < 0.8) continue;
    // Una caja que ocupa media tarjeta no es un número: es el código de barras
    // o una franja que el OCR agrupó mal.
    if ((w.bbox.x1 - w.bbox.x0) > anchoImagen * 0.3) continue;

    const esFormatoTramite = d.length >= 10;
    // A la derecha del rótulo es a la derecha de donde el rótulo TERMINA. Con el
    // borde izquierdo, cualquier número de la mitad derecha de la tarjeta caía
    // dentro.
    const juntoAlRotulo = rotulos.some((r) => mismaLinea(w, r) && w.bbox.x0 >= r.bbox.x1);
    if (!esFormatoTramite && !juntoAlRotulo) continue;

    rects.push({
      x: w.bbox.x0 / scale - margin,
      y: w.bbox.y0 / scale - margin,
      w: (w.bbox.x1 - w.bbox.x0) / scale + margin * 2,
      h: (w.bbox.y1 - w.bbox.y0) / scale + margin * 2,
    });
  }
  return rects;
}

/**
 * Copia independiente del canvas. La foto original se guarda intacta y la
 * redacción se aplica sobre una copia: así se puede recalcular cuál es el Nº de
 * Trámite cuando aparece un dato nuevo —el número de documento del titular, que
 * es justamente el que NO hay que tapar, puede llegar después de la foto, al
 * leerse el dorso— sin haber quemado ya la imagen equivocada.
 */
export function cloneCanvas(c: HTMLCanvasElement): HTMLCanvasElement {
  const copia = document.createElement("canvas");
  copia.width = c.width;
  copia.height = c.height;
  copia.getContext("2d")!.drawImage(c, 0, 0);
  return copia;
}

/** Relleno negro DESTRUCTIVO sobre el canvas (la redacción no es una capa). */
export function redactRegions(c: HTMLCanvasElement, rects: RedactRect[]): void {
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#000";
  for (const r of rects) ctx.fillRect(r.x, r.y, r.w, r.h);
}

export function canvasToJpegBase64(c: HTMLCanvasElement, quality = 0.85): string {
  const dataUrl = c.toDataURL("image/jpeg", quality);
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

export interface DataRegion {
  rect: RedactRect;
  /** Qué dato se reconoció ahí, para rotularlo en la vista previa. */
  label: string;
}

/**
 * Ubica en la foto los datos del autor que el OCR llegó a reconocer, para
 * poder señalarlos.
 *
 * No valida nada: es para que quien manda la foto VEA qué se entendió de ella.
 * Un "no se pudo leer" sin más deja a la persona probando de nuevo a ciegas;
 * mostrar el número recuadrado y el apellido sin recuadrar dice qué arreglar.
 */
export interface EtiquetasDeDatos {
  numero: string;
  apellido: string;
  nombre: string;
  nacimiento: string;
  sexo: string;
  nacionalidad: string;
  vencimiento: string;
}

/** Palabras agrupadas por renglón: dos palabras están en la misma línea si sus
 *  cajas se solapan en vertical más de la mitad de la más baja. */
function porRenglones(words: OcrWord[]): OcrWord[][] {
  const orden = [...words].sort((a, b) => a.bbox.y0 - b.bbox.y0 || a.bbox.x0 - b.bbox.x0);
  const renglones: OcrWord[][] = [];
  for (const w of orden) {
    const alto = w.bbox.y1 - w.bbox.y0;
    const centro = (w.bbox.y0 + w.bbox.y1) / 2;
    const encaja = renglones.find((r) => {
      const ref = r[r.length - 1];
      return centro >= ref.bbox.y0 - alto * 0.5 && centro <= ref.bbox.y1 + alto * 0.5;
    });
    if (encaja) encaja.push(w);
    else renglones.push([w]);
  }
  for (const r of renglones) r.sort((a, b) => a.bbox.x0 - b.bbox.x0);
  return renglones;
}

function unir(ws: OcrWord[]): RedactRect {
  return {
    x: Math.min(...ws.map((w) => w.bbox.x0)),
    y: Math.min(...ws.map((w) => w.bbox.y0)),
    w: Math.max(...ws.map((w) => w.bbox.x1)) - Math.min(...ws.map((w) => w.bbox.x0)),
    h: Math.max(...ws.map((w) => w.bbox.y1)) - Math.min(...ws.map((w) => w.bbox.y0)),
  };
}

/**
 * Ubica en la foto los datos del documento, para poder señalarlos.
 *
 * No valida nada: es para que quien manda la foto VEA qué se entendió de ella.
 * Un "no se pudo leer" sin más deja a la persona probando de nuevo a ciegas.
 *
 * Dos cosas que la versión anterior hacía mal y se notaban en pantalla:
 *
 *  - Exigía que UNA SOLA palabra del OCR fuera exactamente el dato. "27.391.444"
 *    sale partido en "27.391." + "444" con frecuencia, y entonces no marcaba
 *    nada aunque el número estuviera perfectamente leído — la pantalla decía
 *    "no pudimos ubicar ningún dato" sobre una foto que se había verificado.
 *    Ahora prueba también grupos de hasta tres palabras seguidas del renglón.
 *
 *  - Solo marcaba lo que coincidía con los datos cargados de la persona. El
 *    sexo, la nacionalidad y el vencimiento los dice el documento y no están en
 *    el formulario, así que no se marcaban nunca. Se buscan por su rótulo, y el
 *    valor está DEBAJO del rótulo, no al lado: el frente es una grilla.
 */
export function findDataRegions(
  words: OcrWord[],
  datos: { dni: string; lastName: string; firstName: string; dateOfBirth: string },
  etiquetas: EtiquetasDeDatos,
  scale = 1
): DataRegion[] {
  const norm = (t: string) =>
    t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z]/g, "");
  const digitosDe = (t: string) => t.replace(/\D/g, "").replace(/^0+/, "");
  const escalar = (r: RedactRect): RedactRect => ({
    x: r.x / scale, y: r.y / scale, w: r.w / scale, h: r.h / scale,
  });

  const renglones = porRenglones(words);
  const encontradas: DataRegion[] = [];
  const usadas = new Set<OcrWord>();

  /** Primer grupo de hasta `maxPalabras` seguidas que cumple `cumple`. */
  const marcar = (
    label: string,
    cumple: (texto: string) => boolean,
    maxPalabras = 3
  ): boolean => {
    for (const renglon of renglones) {
      for (let i = 0; i < renglon.length; i++) {
        for (let n = 1; n <= maxPalabras && i + n <= renglon.length; n++) {
          const grupo = renglon.slice(i, i + n);
          if (grupo.some((w) => usadas.has(w))) continue;
          if (!cumple(grupo.map((w) => w.text).join(""))) continue;
          grupo.forEach((w) => usadas.add(w));
          encontradas.push({ label, rect: escalar(unir(grupo)) });
          return true;
        }
      }
    }
    return false;
  };

  // Primero, lo que DICE el documento, por su rótulo. No depende de que haya
  // datos cargados, y eso importa: creando un autor nuevo el formulario está
  // vacío, y antes eso dejaba la foto sin una sola marca aunque el OCR hubiera
  // leído todo. Las marcas son para que la persona vea qué se entendió de la
  // foto, no para mostrar en qué coincide con el formulario.
  const porRotulo: [string, DefinicionDeRotulo, (w: OcrWord) => boolean, number][] = [
    [etiquetas.apellido, ROTULOS.apellido, (w) => /^[A-ZÁÉÍÓÚÑ]{2,}$/i.test(w.text.trim()), 3],
    [etiquetas.nombre, ROTULOS.nombres, (w) => /^[A-ZÁÉÍÓÚÑ]{2,}$/i.test(w.text.trim()), 3],
    [etiquetas.numero, ROTULOS.documento, (w) => /\d{2}/.test(w.text), 2],
    [etiquetas.nacimiento, ROTULOS.nacimiento, (w) => /\d/.test(w.text), 4],
    [etiquetas.vencimiento, ROTULOS.vencimiento, (w) => /\d/.test(w.text), 4],
    [etiquetas.sexo, ROTULOS.sexo, (w) => /^[MFX]$/i.test(w.text.trim()), 1],
    [etiquetas.nacionalidad, ROTULOS.nacionalidad, (w) => /^[A-ZÁÉÍÓÚÑ]{5,}$/i.test(w.text.trim()), 1],
  ];

  for (const [label, esRotulo, sirve, maxPalabras] of porRotulo) {
    const hallado = valorBajoDeRotulo(words as PalabraOcr[], esRotulo, sirve, maxPalabras);
    if (!hallado) continue;
    const suyas = hallado.palabras as OcrWord[];
    if (suyas.some((w) => usadas.has(w))) continue;
    suyas.forEach((w) => usadas.add(w));
    encontradas.push({ label, rect: escalar(unir(suyas)) });
  }

  // Y después, lo que además coincide con los datos cargados. Cubre los casos
  // donde el rótulo no se leyó pero el valor sí.
  const dni = digitosDe(datos.dni);
  const apellido = norm(datos.lastName);
  const nombres = norm(datos.firstName);
  const anio = String(datos.dateOfBirth).slice(0, 4);

  if (dni.length > 0) marcar(etiquetas.numero, (t) => digitosDe(t) === dni);
  if (apellido.length >= 3) marcar(etiquetas.apellido, (t) => norm(t) === apellido);
  if (nombres.length >= 3) {
    // Los nombres pueden venir en varias palabras: se marca cada una.
    for (const nombre of datos.firstName.split(/\s+/).filter((n) => norm(n).length >= 3)) {
      marcar(etiquetas.nombre, (t) => norm(t) === norm(nombre));
    }
  }
  if (anio.length === 4) marcar(etiquetas.nacimiento, (t) => digitosDe(t).includes(anio), 4);

  return encontradas;
}

/** Dibuja los recuadros sobre una COPIA de vista previa (nunca sobre el envío). */
export function drawDataRegions(
  preview: HTMLCanvasElement,
  regiones: DataRegion[],
  escalaPreview: number
): void {
  const ctx = preview.getContext("2d")!;
  ctx.save();
  ctx.lineWidth = Math.max(1.5, 2 * escalaPreview);
  ctx.strokeStyle = "#2e7d32";
  ctx.fillStyle = "#2e7d32";
  ctx.font = `${Math.max(9, Math.round(11 * escalaPreview))}px sans-serif`;
  for (const r of regiones) {
    const x = r.rect.x * escalaPreview;
    const y = r.rect.y * escalaPreview;
    const w = r.rect.w * escalaPreview;
    const h = r.rect.h * escalaPreview;
    ctx.strokeRect(x, y, w, h);
    const alto = Math.max(10, Math.round(12 * escalaPreview));
    const ancho = ctx.measureText(r.label).width + 6;
    // La etiqueta va arriba del recuadro, salvo que no haya lugar.
    const ty = y - alto >= 0 ? y - alto : y + h;
    ctx.fillRect(x, ty, ancho, alto);
    ctx.fillStyle = "#fff";
    ctx.fillText(r.label, x + 3, ty + alto - 3);
    ctx.fillStyle = "#2e7d32";
  }
  ctx.restore();
}

/**
 * Zonas del dorso donde viven los datos escritos, cada una con el preproceso
 * que le hace falta. No es capricho: medido sobre fotos reales, el domicilio
 * solo aparece recortando la mitad de arriba, y el CUIL solo con la banda del
 * medio ampliada al cuádruple y con el contraste estirado. Con el preproceso
 * del MRZ —que es el de la franja de abajo— ninguno de los dos se lee.
 */
export function topHalfCanvas(c: HTMLCanvasElement, fraction = 0.55): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = c.width * 2;
  out.height = Math.round(c.height * fraction) * 2;
  const ctx = out.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(c, 0, 0, c.width, Math.round(c.height * fraction), 0, 0, out.width, out.height);
  return aGrises(out);
}

export function cuilBandCanvas(c: HTMLCanvasElement): HTMLCanvasElement {
  const desde = Math.round(c.height * 0.45);
  const alto = Math.round(c.height * 0.17);
  const out = document.createElement("canvas");
  out.width = c.width * 4;
  out.height = alto * 4;
  const ctx = out.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(c, 0, desde, c.width, alto, 0, 0, out.width, out.height);
  return estirarContraste(aGrises(out));
}

function aGrises(c: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = c.getContext("2d")!;
  const d = ctx.getImageData(0, 0, c.width, c.height);
  const px = d.data;
  for (let i = 0; i < px.length; i += 4) {
    const g = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
    px[i] = px[i + 1] = px[i + 2] = g;
  }
  ctx.putImageData(d, 0, 0);
  return c;
}

function estirarContraste(c: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = c.getContext("2d")!;
  const d = ctx.getImageData(0, 0, c.width, c.height);
  const px = d.data;
  let min = 255;
  let max = 0;
  for (let i = 0; i < px.length; i += 4) {
    if (px[i] < min) min = px[i];
    if (px[i] > max) max = px[i];
  }
  const rango = Math.max(1, max - min);
  for (let i = 0; i < px.length; i += 4) {
    const v = ((px[i] - min) * 255) / rango;
    px[i] = px[i + 1] = px[i + 2] = v;
  }
  ctx.putImageData(d, 0, 0);
  return c;
}
